import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { fmtCurrency, fmtDateTime, STATUS_TR } from "@/utils/format";
import {
  FileDown, Warehouse as WhIcon, Factory, Package, Clock, Check, Truck, Split,
} from "lucide-react";
import AttachmentList from "@/components/AttachmentList";
import AuditLogPanel from "@/components/AuditLogPanel";

function StatusBadge({ status }) {
  const cls = {
    pending: "border-neutral-500 text-neutral-700 bg-neutral-100",
    ready: "border-[#0055FF] text-[#0055FF] bg-blue-50",
    completed: "border-[#00A650] text-[#00A650] bg-green-50",
    shipped: "border-[#00A650] bg-[#00A650] text-white",
  }[status] || "border-neutral-400 text-neutral-600";
  return (
    <span className={`inline-block px-2 py-0.5 text-[10px] font-mono uppercase border ${cls}`}>
      {STATUS_TR[status] || status}
    </span>
  );
}

function AllocBadge({ alloc }) {
  return alloc === "warehouse" ? (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono uppercase border border-[#0055FF] text-[#0055FF]">
      <WhIcon size={10} /> Depo
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono uppercase border border-[#FF3B30] text-[#FF3B30]">
      <Factory size={10} /> Üretim
    </span>
  );
}

export default function OrderDetailsDialog({ orderId, open, onOpenChange, canEditDueDate, onChanged }) {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    if (!orderId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/orders/${orderId}`);
      setOrder(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (open) load();
    else {
      // reset when closing to avoid stale DOM on re-open
      setOrder(null);
    }
    /* eslint-disable-next-line */
  }, [open, orderId]);

  const downloadPdf = async () => {
    if (!order) return;
    try {
      const res = await api.get(`/orders/${order.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${order.order_number}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const updateDueDate = async (item, date) => {
    if (!item?.id) {
      toast.error("Kalem kimliği eksik — sayfayı yenileyin (F5)");
      return;
    }
    try {
      const url = `/orders/${order.id}/items/${item.id}/due-date` + (date ? `?date=${date}` : "?date=");
      const { data } = await api.patch(url);
      setOrder(data);
      onChanged?.();
      toast.success("Termin güncellendi");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const today = new Date().toISOString().slice(0, 10);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none border-2 border-[#09090B] bg-white sm:max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl tracking-tight" data-testid="order-details-title">
            {order?.order_number || "Sipariş Detayı"}
          </DialogTitle>
        </DialogHeader>

        {loading || !order ? (
          <div className="p-8 text-center text-sm font-mono text-neutral-500">Yükleniyor…</div>
        ) : (
          <div className="space-y-4">
            {/* Summary */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="border-2 border-[#09090B] p-3">
                <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">Müşteri</div>
                <div className="font-bold text-sm">{order.customer_name}</div>
              </div>
              <div className="border-2 border-[#09090B] p-3">
                <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">Teklif No</div>
                <div className="font-mono font-bold">{order.quote_number}</div>
              </div>
              <div className="border-2 border-[#09090B] p-3">
                <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">Tarih</div>
                <div className="font-mono text-sm">{fmtDateTime(order.created_at)}</div>
              </div>
              <div className="border-2 border-[#09090B] p-3">
                <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">Durum</div>
                <StatusBadge status={order.status} />
              </div>
              <div className="border-2 border-[#09090B] p-3 md:col-span-4" data-testid="order-detail-payment-info">
                <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">Ödeme Bilgisi</div>
                <div className="text-sm">
                  {order.payment_info
                    ? <span className="font-semibold">{order.payment_info}</span>
                    : <span className="text-neutral-400 italic">—</span>}
                </div>
              </div>
            </div>

            {/* Items */}
            <div className="border-2 border-[#09090B]">
              <Table>
                <TableHeader>
                  <TableRow className="bg-neutral-100 border-b-2 border-[#09090B]">
                    <TableHead className="font-mono uppercase text-xs">SKU</TableHead>
                    <TableHead className="font-mono uppercase text-xs">Ürün</TableHead>
                    <TableHead className="font-mono uppercase text-xs">Sevk</TableHead>
                    <TableHead className="font-mono uppercase text-xs text-right">Miktar</TableHead>
                    <TableHead className="font-mono uppercase text-xs text-right">B.Fiyat</TableHead>
                    <TableHead className="font-mono uppercase text-xs text-right">Toplam</TableHead>
                    <TableHead className="font-mono uppercase text-xs">Durum</TableHead>
                    <TableHead className="font-mono uppercase text-xs">Termin</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {order.items.map((it, i) => {
                    const overdue = it.due_date && it.status !== "completed" && it.due_date < today && it.status !== "shipped";
                    return (
                      <TableRow key={i} data-testid={`order-item-${i}`}>
                        <TableCell className="font-mono text-sm">{it.sku}</TableCell>
                        <TableCell>
                          <div className="font-semibold text-sm flex items-center gap-2">
                            <Package size={12} /> {it.product_name}
                          </div>
                          {it.notes && (
                            <div
                              className="mt-1 text-xs text-neutral-600 whitespace-pre-wrap italic border-l-2 border-neutral-300 pl-2"
                              data-testid={`order-item-notes-${i}`}
                            >
                              {it.notes}
                            </div>
                          )}
                          {it.split_note && (
                            <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-neutral-500 px-1.5 py-0.5 border border-neutral-300">
                              <Split size={10} /> {it.split_note}
                            </div>
                          )}
                          {it.waybill_number && (
                            <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-mono uppercase text-[#00A650]">
                              <Truck size={10} /> {it.waybill_number}
                            </div>
                          )}
                        </TableCell>
                        <TableCell><AllocBadge alloc={it.allocation} /></TableCell>
                        <TableCell className="text-right font-mono">{it.quantity} {it.unit}</TableCell>
                        <TableCell className="text-right font-mono text-sm">{fmtCurrency(it.unit_price)}</TableCell>
                        <TableCell className="text-right font-mono">{fmtCurrency(it.line_total)}</TableCell>
                        <TableCell><StatusBadge status={it.status} /></TableCell>
                        <TableCell>
                          {it.allocation === "production" ? (
                            canEditDueDate && it.status !== "shipped" ? (
                              <Input
                                type="date"
                                value={it.due_date || ""}
                                onChange={(e) => updateDueDate(it, e.target.value)}
                                className={`h-8 w-36 rounded-none border-2 font-mono text-xs ${overdue ? "border-[#FF3B30] text-[#FF3B30]" : "border-[#09090B]"}`}
                                data-testid={`detail-due-input-${i}`}
                              />
                            ) : (
                              it.due_date ? (
                                <span className={`inline-flex items-center gap-1 text-xs font-mono px-1.5 py-0.5 border ${overdue ? "border-[#FF3B30] text-[#FF3B30] font-bold" : "border-neutral-300 text-neutral-600"}`}>
                                  <Clock size={10} /> {it.due_date}
                                </span>
                              ) : (
                                <span className="text-neutral-400 text-xs font-mono">—</span>
                              )
                            )
                          ) : (
                            <span className="text-neutral-400 text-xs font-mono">—</span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            {/* Totals */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="border-2 border-[#09090B] p-4">
                <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-2">Notlar</div>
                <div className="text-sm whitespace-pre-line text-neutral-700">
                  {order.notes || <span className="text-neutral-400 italic">yok</span>}
                </div>
              </div>
              <div className="border-2 border-[#09090B] p-4 space-y-1 text-right">
                <div className="flex justify-between font-mono">
                  <span className="text-neutral-500">Ara Toplam</span>
                  <span>{fmtCurrency(order.subtotal)}</span>
                </div>
                <div className="flex justify-between font-mono">
                  <span className="text-neutral-500">KDV %{order.tax_rate}</span>
                  <span>{fmtCurrency(order.tax_amount)}</span>
                </div>
                <div className="flex justify-between font-mono text-xl font-bold pt-2 border-t-2 border-[#09090B]">
                  <span>GENEL</span>
                  <span data-testid="detail-total">{fmtCurrency(order.total)}</span>
                </div>
              </div>
            </div>

            <AuditLogPanel entity="order" entityId={order.id} open={open} />
          </div>
        )}

        {order && order.attachments && order.attachments.length > 0 && (
          <div className="mt-4">
            <AttachmentList
              endpoint={`/orders/${order.id}/attachments`}
              attachments={order.attachments}
            />
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            className="rounded-none border-2 border-[#09090B]"
            onClick={() => onOpenChange(false)}
          >
            Kapat
          </Button>
          <Button
            className="rounded-none bg-[#09090B] hover:bg-[#0055FF]"
            onClick={downloadPdf}
            disabled={!order}
            data-testid="detail-pdf-btn"
          >
            <FileDown size={16} className="mr-2" /> PDF İndir
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
